import { createElement, FC, useCallback, useEffect, useState } from 'react';
import { List, ListProps } from './list';
import { useWebgate } from '@xvia/webgate-connect';
import { getServerData } from '../../util/get-server-data';
import { Catalog, search } from '../../apis/search-catalog';
import { useHistory, useLocation } from 'react-router-dom';
import { getCategorys, ICategory } from '../../apis/categorys';
import { getDepartments, IDepartment } from '../../apis/department';
import { ROUTES } from '../../routes';

const striptags = require('striptags');
const portalUrl = process.env.PORTAL_URL;
const domain = portalUrl?.replace('https://', '')?.replace('http://', '');

export interface Category {
  description: string;
  hidden: boolean;
  icon: string;
  id: string;
  name: string;
  orderIndex: number;
  slug: string;
}
export type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug'>;

type Filters = {
  category: string[];
  department: string[];
  isOnline?: boolean;
  query: string;
  page: number;
};

export const SERVICE_FILTERS_KEY = 'service-filters';

export const usePersistedFilter = () => {
  const location = useLocation();

  const getInitialFilters = (): Filters => {
    const params = new URLSearchParams(location.search);
    const microfrontend = params.get('mf');

    if (typeof window !== 'undefined') {
      if (microfrontend) {
        sessionStorage.removeItem(SERVICE_FILTERS_KEY);
      }

      const storagedFilter = sessionStorage.getItem(SERVICE_FILTERS_KEY);

      if (storagedFilter) {
        try {
          return JSON.parse(storagedFilter);
        } catch {
          console.log('Filter not loaded from storage');
        }
      }
    }

    const category = params.get('category') ?? '';
    const department = params.get('department') ?? params.get('orgao') ?? '';

    return {
      category: category ? [category] : [],
      department: department ? [department] : [],
      query: '',
      page: 1,
    };
  };

  const [filters, setFiltersState] = useState<Filters>(getInitialFilters);

  const setFilters = (newFilters: Filters) => {
    setFiltersState(newFilters);
    sessionStorage.setItem(SERVICE_FILTERS_KEY, JSON.stringify(newFilters));
  };

  useEffect(() => {
    const storagedFilter = sessionStorage.getItem(SERVICE_FILTERS_KEY);
    if (!storagedFilter) {
      const params = new URLSearchParams(location.search);
      const category = params.get('category') ?? '';
      const department = params.get('department') ?? params.get('orgao') ?? '';

      const updatedFilters = {
        category: category ? [category] : [],
        department: department ? [department] : [],
        query: '',
        page: 1,
      };

      setFilters(updatedFilters);
    }
  }, [location.key]);

  return [filters, setFilters] as const;
};

export const ListContainer: FC = () => {
  const { serverData, portalConnect } = useWebgate();

  const history = useHistory();
  const [filters, setFilters] = usePersistedFilter();

  const [categoryList, setCategoryList] = useState<
    ICategory[] | null | undefined
  >(serverData?.categoryList || getServerData('categoryList', null));
  const [catalogList, setCatalogList] = useState<any[]>(
    serverData?.catalogList || getServerData('catalogList', null)
  );
  const [departmentList, setDepartmentList] = useState<
    IDepartment[] | null | undefined
  >(serverData?.departmentList || getServerData('departmentList', null));
  const [resultTotal, setResultTotal] = useState<number>();
  const [isOpen, setIsOpen] = useState(false);

  const disposition: 'line' | 'card' = 'card';

  const PAGE_SIZE = 15;

  const [inputSearch, setInputSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryFilter[]
  >([]);
  const [selectedDepartments, setSelectedDepartments] = useState<
    CategoryFilter[]
  >([]);

  const getCategoryServer = useCallback(() => {
    getCategorys().then(setCategoryList).catch(console.error);
  }, []);

  const getDepartmentServer = useCallback(() => {
    getDepartments().then(setDepartmentList).catch(console.error);
  }, []);

  const openLink = useCallback(
    (url: string) => {
      if (!url) return;
      const isPortalApp =
        new RegExp(`^(https?://.*${domain})?/app/`).test(`${portalUrl}/app/`) ||
        url.startsWith('/app/');
      if (!isPortalApp) {
        window.open(url, '_blank');
        return;
      }
      portalConnect?.redirectTo(url);
    },
    [portalConnect]
  );

  const onClickCard = useCallback(
    (catalog: Catalog) =>
      history.push(`${ROUTES.SERVICE_LIST_PAGE}/${catalog.slug}`),
    [history]
  );

  const toggleDrawer = (open: boolean) => () => {
    setIsOpen(open);
  };

  const handleCheckboxChange = (currentCategory: CategoryFilter) => {
    const updatedCategories = [...selectedCategories];
    const categoryIndex = updatedCategories.findIndex(
      ({ slug }) => slug === currentCategory.slug
    );

    if (categoryIndex === -1) {
      updatedCategories.push(currentCategory);
    } else {
      updatedCategories.splice(categoryIndex, 1);
    }

    setSelectedCategories(updatedCategories);
  };

  const handleDepartmentCheckboxChange = (
    currentDepartment: CategoryFilter
  ) => {
    const updatedDepartments = [...selectedDepartments];
    const departmentIndex = updatedDepartments.findIndex(
      ({ slug }) => slug === currentDepartment.slug
    );

    if (departmentIndex === -1) {
      updatedDepartments.push(currentDepartment);
    } else {
      updatedDepartments.splice(departmentIndex, 1);
    }

    setSelectedDepartments(updatedDepartments);
  };

  const handleFilterConfirm = () => {
    const category = selectedCategories.map(({ slug }) => slug);
    const department = selectedDepartments.map(({ slug }) => slug);

    setIsOpen(false);
    setFilters({
      ...filters,
      category,
      department,
      page: 1,
    });
  };

  const handleTabChange = (tab: 'all' | 'online') => {
    setFilters({
      ...filters,
      isOnline: tab === 'online' ? true : undefined,
      page: 1,
    });
  };

  const handleSearch = useCallback(() => {
    const category = filters.category.length ? filters.category : undefined;
    const departmentSlug = filters.department.length
      ? filters.department
      : undefined;
    const currentPage = filters.page ?? 1;
    const offset = (currentPage - 1) * PAGE_SIZE;

    const params = {
      query: filters.query,
      groups: ['CATALOG'],
      category,
      departmentSlug,
      isOnline: filters.isOnline,
      from: offset,
      size: PAGE_SIZE,
    };

    search(params)
      .then((res) => {
        setCatalogList(res);
        setResultTotal(res[0]?.resultTotal ?? 0);
      })
      .catch(console.error);
  }, [filters]);

  const handleClickSearch = useCallback(() => {
    setFilters({
      ...filters,
      query: inputSearch,
      page: 1,
    });
  }, [filters, inputSearch, setFilters]);

  const handleChangeText = useCallback((text: string) => {
    setInputSearch(text);
  }, []);

  const handlePaginationChange = (_: any, currentPage: number) => {
    if (typeof window !== 'undefined' && window.scrollY) window.scrollTo(0, 0);
    setFilters({
      ...filters,
      page: currentPage,
    });
  };

  const removeCategoryFilter = useCallback(
    (slugToRemove: string) => {
      setFilters({
        ...filters,
        category: filters.category.filter((slug) => slug !== slugToRemove),
        page: 1,
      });
    },
    [filters, setFilters]
  );

  const removeDepartmentFilter = useCallback(
    (slugToRemove: string) => {
      setFilters({
        ...filters,
        department: filters.department.filter((slug) => slug !== slugToRemove),
        page: 1,
      });
    },
    [filters, setFilters]
  );

  const clearCategoriesFilter = useCallback(() => {
    setSelectedCategories([]);
  }, []);

  const clearDepartmentsFilter = useCallback(() => {
    setSelectedDepartments([]);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      category: [],
      department: [],
      page: 1,
      query: '',
      isOnline: undefined,
    });
  }, [setFilters]);

  useEffect(() => {
    if (!categoryList) {
      getCategoryServer();
    }
    if (!departmentList) {
      getDepartmentServer();
    }
  }, [categoryList, departmentList, getCategoryServer, getDepartmentServer]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    setInputSearch(filters.query);
  }, [filters.query]);

  useEffect(() => {
    setSelectedCategories(
      categoryList
        ?.filter(({ slug }) => filters.category.includes(slug))
        ?.map(({ id, name, slug }) => ({ id, name, slug })) ?? []
    );
  }, [categoryList, filters.category]);

  useEffect(() => {
    setSelectedDepartments(
      departmentList
        ?.filter(({ slug }) => filters.department.includes(slug))
        ?.map(({ id, name, slug }) => ({ id, name, slug })) ?? []
    );
  }, [departmentList, filters.department]);

  const listProps: ListProps = {
    list: catalogList?.map((c) => ({
      ...c,
      description: striptags(c.description),
    })) as Catalog[],
    onClick: onClickCard,
    onClickFavourite: () => {},
    onClickOnlineService: openLink,
    disposition,
    resultTotal,
    page: filters.page,
    handleChange: handlePaginationChange,
    toggleDrawer,
    open: isOpen,
    selectedCategories,
    selectedDepartments,
    appliedCategories: selectedCategories,
    appliedDepartments: selectedDepartments,
    handleCheckboxChange,
    handleDepartmentCheckboxChange,
    categories: categoryList ?? [],
    departments: departmentList ?? [],
    handleFilterByCategory: handleFilterConfirm,
    totalPages: resultTotal ? Math.ceil(resultTotal / PAGE_SIZE) : 0,
    removeCategoryFilter,
    removeDepartmentFilter,
    onClickSearch: handleClickSearch,
    onChangeText: handleChangeText,
    value: inputSearch,
    activeTab: filters.isOnline ? 'online' : 'all',
    onTabChange: handleTabChange,
    clearFilters,
    clearCategoriesFilter,
    clearDepartmentsFilter,
  };

  return createElement(List, listProps);
};
